import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

export default function SolicitantesTab() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [nome, setNome] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/solicitantes"); setList(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setNome(""); setUsername(""); setPassword(""); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setNome(s.nome); setUsername(s.username); setPassword(""); setOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        const payload = { nome, username };
        if (password) payload.password = password;
        await api.put(`/solicitantes/${editing.id}`, payload);
        toast.success("Solicitante atualizado");
      } else {
        if (!nome || !username || !password) { toast.error("Preencha todos os campos"); return; }
        await api.post("/solicitantes", { nome, username, password });
        toast.success("Solicitante criado");
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este solicitante?")) return;
    try { await api.delete(`/solicitantes/${id}`); toast.success("Excluído"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="font-heading">Solicitantes</CardTitle>
          <CardDescription>Cadastre os usuários que poderão fazer pedidos.</CardDescription>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-solicitante-btn" onClick={openNew} className="btn-brand rounded-full">
              <Plus className="h-4 w-4 mr-2" /> Novo solicitante
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Editar solicitante" : "Novo solicitante"}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2"><Label>Nome</Label>
                <Input data-testid="sol-nome-input" value={nome} onChange={(e) => setNome(e.target.value)} /></div>
              <div className="space-y-2"><Label>Usuário</Label>
                <Input data-testid="sol-username-input" value={username} onChange={(e) => setUsername(e.target.value)} /></div>
              <div className="space-y-2"><Label>{editing ? "Nova senha (deixe em branco para manter)" : "Senha"}</Label>
                <Input data-testid="sol-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={save} data-testid="sol-save-btn" className="btn-brand">Salvar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Nome</TableHead><TableHead>Usuário</TableHead><TableHead className="w-32">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-slate-500 py-8">Nenhum solicitante cadastrado.</TableCell></TableRow>}
              {list.map((s, i) => (
                <TableRow key={s.id} className="row-fade" style={{ animationDelay: `${i * 30}ms` }}>
                  <TableCell className="font-medium">{s.nome}</TableCell>
                  <TableCell className="text-slate-600">{s.username}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`edit-sol-${s.id}`}><Pencil className="h-3 w-3" /></Button>
                      <Button size="sm" variant="outline" onClick={() => remove(s.id)} data-testid={`del-sol-${s.id}`}><Trash2 className="h-3 w-3 text-red-500" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
